import { CONFIRMATION_TYPES, TRIGGER_TYPES } from "Components/common/constants";
import { TriggerType } from "./types";

/**
 * Creates a new mission stop with default values
 */
export const createNewMissionStop = (order: number): MissionStop => ({
  id: "",
  name: "",
  duration: 0,
  latitude: 0,
  longitude: 0,
  hitchType: 0,
  confirmationType: CONFIRMATION_TYPES.CONFIRMATION_TYPE_NONE,
  confirmationButtonsRequired: 1,
  missionStopVdaActions: [],
  stopPk: "",
  deletedAt: undefined,
  order,
});

/**
 * Creates a new VDA action with default values
 */
export const createNewVdaAction = (
  order: number,
  defaultActionType: string,
  actionDescription?: string
): VdaAction => {
  return {
    actionId: null,
    actionType: defaultActionType,
    actionDescription: actionDescription ?? null,
    blockingType: "HARD",
    actionParameters: [],
    missionId: null,
    stopId: null,
    queuedMissionStopId: null,
    mapMissionStopId: null,
    order,
    metadata: null,
    actionStatus: null,
  };
};

/**
 * Validates if a station is valid for selection
 */
export const isValidStation = (station: any): boolean => {
  return Boolean(station && station.id && station.name);
};

/**
 * Validates mission stops for double confirmation rules
 */
export const validateMissionStops = (missionStops: MissionStop[]): string[] => {
  const errors: string[] = [];

  missionStops.forEach((stop, index) => {
    // Count total trigger types for this stop (including duplicates)
    const totalTriggerTypesCount = countTotalTriggerTypesForStop(stop);

    // Check if we exceed the maximum number of trigger types
    if (totalTriggerTypesCount > 3) {
      errors.push(
        `Stop ${
          index + 1
        } has more than 3 trigger types. Maximum of 3 trigger types allowed per stop.`
      );
    }

    // Check if stop has double confirmation but also has VDA actions
    if (
      stop.confirmationType ===
        CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION &&
      stop.missionStopVdaActions &&
      stop.missionStopVdaActions.length > 0
    ) {
      errors.push(
        `Stop ${
          index + 1
        } has Double confirmation selected but also contains VDA actions. Double confirmation must be the only action for a stop.`
      );
    }
  });

  return errors;
};

/**
 * Validates that all stations have been selected
 */
export const validateStationSelections = (
  missionStops: MissionStop[]
): string[] => {
  const stationsWithoutValidStop = missionStops.filter(
    (stop) => !stop.stopPk || stop.stopPk.trim() === ""
  );

  if (stationsWithoutValidStop.length > 0) {
    return ["All stations must have a valid station selected"];
  }

  return [];
};

/**
 * Counts unhitch events in mission stops
 */
export const countUnhitchEvents = (missionStops: MissionStop[]): number => {
  return missionStops.filter((stop) => stop.hitchType === 2).length;
};

/**
 * Builds the mission payload for API submission
 */
export const buildMissionPayload = (
  missionName: string,
  missionStops: MissionStop[],
  semanticMapId: string,
  timeStamp?: string | number | null
): any => {
  // Process mission stops to set id equal to stopPk for new stops
  const processedStops = missionStops.map((stop) => {
    // If this is a new stop (id is empty) and has a stopPk, set id equal to stopPk
    if ((!stop.id || stop.id === "") && stop.stopPk) {
      return {
        ...stop,
        id: stop.stopPk,
      };
    }
    return stop;
  });

  return {
    timeStamp: timeStamp ? String(timeStamp) : null,
    previous: "Continue",
    name: missionName || `Created ${new Date().toLocaleDateString()}`,
    stops: processedStops,
    semanticMap: semanticMapId,
  };
};

/**
 * Gets available trigger types for actions
 */
export const getAvailableTriggerTypes = (
  isStationViewEnabled = true
): TriggerType[] => {
  const triggerTypes: TriggerType[] = [
    {
      key: TRIGGER_TYPES.HMI_BUTTON,
      value: "HMI Button",
    },
    {
      key: TRIGGER_TYPES.DURATION,
      value: "Duration",
    },
  ];

  // Only include double confirmation if station view feature flag is enabled
  if (isStationViewEnabled) {
    triggerTypes.unshift({
      key: TRIGGER_TYPES.DOUBLE_CONFIRMATION,
      value: "Double confirmation",
    });
  }

  return triggerTypes;
};

/**
 * Updates mission stops by adding a new stop
 */
export const addMissionStop = (currentStops: MissionStop[]): MissionStop[] => {
  const newStop = createNewMissionStop(currentStops.length + 1);
  return [...currentStops, newStop];
};

/**
 * Updates mission stops by removing a stop at specific index
 */
export const removeMissionStop = (
  currentStops: MissionStop[],
  stepIndex: number
): MissionStop[] => {
  return currentStops
    .filter((_, index) => index !== stepIndex)
    .map((stop, newIndex) => ({
      ...stop,
      order: newIndex + 1,
    }));
};

/**
 * Updates mission stops by adding a VDA action to a specific stop
 */
export const addVdaActionToStop = (
  currentStops: MissionStop[],
  stepIndex: number,
  defaultActionType: string,
  actionDescription?: string
): MissionStop[] => {
  return currentStops.map((step, index) =>
    index === stepIndex
      ? {
          ...step,
          missionStopVdaActions: [
            ...step.missionStopVdaActions,
            createNewVdaAction(
              step.missionStopVdaActions.length + 1,
              defaultActionType,
              actionDescription
            ),
          ],
        }
      : step
  );
};

/**
 * Updates mission stops by removing double confirmation from a specific stop
 */
export const removeDoubleConfirmationFromStop = (
  currentStops: MissionStop[],
  stepIndex: number
): MissionStop[] => {
  return currentStops.map((step, stepIdx) =>
    stepIdx === stepIndex
      ? {
          ...step,
          confirmationType: CONFIRMATION_TYPES.CONFIRMATION_TYPE_NONE,
          confirmationButtonsRequired: 0,
        }
      : step
  );
};

/**
 * Updates mission stops by removing a VDA action from a specific stop
 */
export const removeVdaActionFromStop = (
  currentStops: MissionStop[],
  stepIndex: number,
  actionIndex: number
): MissionStop[] => {
  return currentStops.map((step, stepIdx) =>
    stepIdx === stepIndex
      ? {
          ...step,
          missionStopVdaActions: step.missionStopVdaActions
            .filter((_, actionIdx) => actionIdx !== actionIndex)
            .map((action, newIndex) => ({
              ...action,
              order: newIndex + 1,
            })),
        }
      : step
  );
};

/**
 * Updates mission stops by selecting a station for a specific stop
 */
export const updateStopWithStation = (
  currentStops: MissionStop[],
  stepIndex: number,
  station: any
): MissionStop[] => {
  if (!isValidStation(station)) {
    console.warn("Invalid station selected:", station);
    return currentStops;
  }

  return currentStops.map((step, index) =>
    index === stepIndex
      ? {
          ...step,
          stopPk: station.id,
          name: station.name,
          latitude: station.latitude || 0,
          longitude: station.longitude || 0,
        }
      : step
  );
};

/**
 * Updates mission stops by selecting an action for a specific stop and action
 */
export const updateStopWithAction = (
  currentStops: MissionStop[],
  stepIndex: number,
  actionIndex: number,
  action: VdaActionDefinition
): MissionStop[] => {
  return currentStops.map((step, stepIdx) =>
    stepIdx === stepIndex
      ? {
          ...step,
          missionStopVdaActions: step.missionStopVdaActions.map(
            (a, actionIdx) =>
              actionIdx === actionIndex
                ? {
                    ...a,
                    actionType: action.actionType,
                    actionDescription: action.description ?? null,
                    actionParameters: a.actionParameters || [],
                  }
                : a
          ),
        }
      : step
  );
};

/**
 * Counts the total number of trigger types for a mission stop (including duplicates)
 */
export const countTotalTriggerTypesForStop = (
  missionStop: MissionStop
): number => {
  let count = 0;

  // Count double confirmation if present
  if (
    missionStop.confirmationType ===
    CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION
  ) {
    count++;
  }

  // Count trigger types from VDA actions
  missionStop.missionStopVdaActions.forEach((action) => {
    if (action.actionParameters && action.actionParameters.length > 0) {
      const triggerType = action.actionParameters[0].triggerType;
      if (triggerType) {
        count++;
      }
    }
  });

  return count;
};

/** Message shown when adding a trigger type would exceed the 3-per-stop limit */
export const TRIGGER_TYPE_LIMIT_EXCEEDED_MESSAGE =
  "Cannot add more trigger types. Maximum of 3 trigger types allowed per stop.";

/** Message shown when Double Confirmation is incompatible with other trigger types */
export const DOUBLE_CONFIRMATION_EXCLUSIVITY_MESSAGE =
  "Double Confirmation cannot be combined with other trigger types. Please remove other trigger types first.";

/**
 * Returns true if the action at actionIndex already has a trigger type (edit/replace scenario).
 * Returns false if the action has no trigger type yet (add scenario), or if actionIndex
 * is out of range, or if actionParameters is absent/malformed.
 * Used to distinguish editing (allowed when at limit) from adding (blocked when at limit).
 */
export const hasExistingTrigger = (
  missionStop: MissionStop,
  actionIndex: number
): boolean => {
  const actions = missionStop.missionStopVdaActions;
  if (
    !Array.isArray(actions) ||
    actionIndex < 0 ||
    actionIndex >= actions.length
  ) {
    return false;
  }

  const actionBeingEdited = actions[actionIndex];
  const params = actionBeingEdited?.actionParameters;
  if (!Array.isArray(params) || params.length === 0 || !params[0]) {
    return false;
  }

  const existingTrigger = params[0].triggerType;
  if (existingTrigger == null) {
    return false;
  }

  return typeof existingTrigger === "string"
    ? existingTrigger.trim().length > 0
    : Boolean(existingTrigger);
};

/**
 * Updates mission stops by selecting a trigger type for a specific stop and action
 */
export const updateStopWithTriggerType = (
  currentStops: MissionStop[],
  stepIndex: number,
  actionIndex: number,
  triggerType: TriggerType
): MissionStop[] => {
  return currentStops.map((step, stepIdx) => {
    if (stepIdx !== stepIndex) return step;

    // Count total trigger types for this stop (including duplicates)
    const totalTriggerTypesCount = countTotalTriggerTypesForStop(step);

    // When EDITING: the action at actionIndex already has a trigger type - we're replacing, not adding.
    // The total count stays the same, so we allow the change.
    // When ADDING: the action has no trigger type yet - block if we'd exceed 3.
    if (!hasExistingTrigger(step, actionIndex) && totalTriggerTypesCount >= 3) {
      console.warn(TRIGGER_TYPE_LIMIT_EXCEEDED_MESSAGE);
      return step;
    }

    // Check for double confirmation status
    const hasDoubleConfirmation =
      step.confirmationType ===
      CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION;

    // Check for other trigger types (HMI Button or Duration) - exclude the current action being edited
    const hasOtherTriggerTypes = (step.missionStopVdaActions || []).some(
      (action, idx) => {
        // Skip the action that is being edited
        if (idx === actionIndex) {
          return false;
        }

        if (action.actionParameters && action.actionParameters.length > 0) {
          const actionTriggerType = action.actionParameters[0].triggerType;
          return (
            actionTriggerType === TRIGGER_TYPES.HMI_BUTTON ||
            actionTriggerType === TRIGGER_TYPES.DURATION
          );
        }
        return false;
      }
    );

    // Validate mutual exclusivity - Double confirmation cannot be combined with any other trigger type
    if (
      triggerType.key === TRIGGER_TYPES.DOUBLE_CONFIRMATION &&
      hasOtherTriggerTypes
    ) {
      console.warn(DOUBLE_CONFIRMATION_EXCLUSIVITY_MESSAGE);
      return step;
    }

    // Handle special "Double confirmation" trigger type
    if (triggerType.key === TRIGGER_TYPES.DOUBLE_CONFIRMATION) {
      return {
        ...step,
        confirmationType: CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION,
        confirmationButtonsRequired: 2,
        // Clear VDA actions when using double confirmation
        missionStopVdaActions: [],
      };
    }

    // Handle regular trigger types for VDA actions
    // If we're switching from double confirmation to a regular trigger type,
    // we need to clear double confirmation and add a new VDA action
    if (hasDoubleConfirmation) {
      return {
        ...step,
        confirmationType: CONFIRMATION_TYPES.CONFIRMATION_TYPE_NONE,
        confirmationButtonsRequired: 0,
        missionStopVdaActions: [
          {
            ...createNewVdaAction(1, "WAIT_FOR_TRIGGER", "Wait for trigger"),
            actionParameters: [
              {
                triggerType: triggerType.key,
                duration: null,
              },
            ],
          },
        ],
      };
    }

    const actions = step.missionStopVdaActions || [];

    // Reject invalid actionIndex: must be 0..length (length = append new action)
    if (actionIndex < 0 || actionIndex > actions.length) {
      console.warn(
        `Invalid actionIndex ${actionIndex} (valid range: 0..${actions.length})`
      );
      return step;
    }

    // Add-case: actionIndex === length — append new action with trigger type
    if (actionIndex === actions.length) {
      return {
        ...step,
        missionStopVdaActions: [
          ...actions,
          {
            ...createNewVdaAction(
              actions.length + 1,
              "WAIT_FOR_TRIGGER",
              "Wait for trigger"
            ),
            actionParameters: [
              {
                triggerType: triggerType.key,
                duration: null,
              },
            ],
          },
        ],
      };
    }

    // Edit-case: actionIndex in range — update existing action
    return {
      ...step,
      missionStopVdaActions: actions.map((a, actionIdx) =>
        actionIdx === actionIndex
          ? {
              ...a,
              actionParameters: [
                {
                  triggerType: triggerType.key,
                  duration: null,
                },
              ],
            }
          : a
      ),
    };
  });
};

/**
 * Updates mission stops by changing duration for a specific stop and action
 */
export const updateStopWithDuration = (
  currentStops: MissionStop[],
  stepIndex: number,
  actionIndex: number,
  duration: string
): MissionStop[] => {
  return currentStops.map((step, stepIdx) =>
    stepIdx === stepIndex
      ? {
          ...step,
          missionStopVdaActions: step.missionStopVdaActions.map(
            (a, actionIdx) =>
              actionIdx === actionIndex
                ? {
                    ...a,
                    actionParameters: a.actionParameters.map(
                      (param, paramIndex) =>
                        paramIndex === 0
                          ? {
                              ...param,
                              duration: parseInt(duration) || null,
                            }
                          : param
                    ),
                  }
                : a
          ),
        }
      : step
  );
};

/**
 * Updates mission stops by changing hitch type for a specific stop
 */
export const updateStopWithHitchType = (
  currentStops: MissionStop[],
  stepIndex: number,
  isUnhitch: boolean
): MissionStop[] => {
  return currentStops.map((step, index) =>
    index === stepIndex ? { ...step, hitchType: isUnhitch ? 2 : 0 } : step
  );
};

/**
 * Reorders mission stops by moving a stop from one index to another
 */
export const reorderMissionStops = (
  currentStops: MissionStop[],
  startIndex: number,
  endIndex: number
): MissionStop[] => {
  // Handle edge cases
  if (!currentStops || currentStops.length === 0) {
    return [];
  }

  // Validate indices
  if (startIndex < 0 || startIndex >= currentStops.length) {
    return currentStops;
  }

  if (endIndex < 0 || endIndex >= currentStops.length) {
    return currentStops;
  }

  // If indices are the same, no reordering needed
  if (startIndex === endIndex) {
    return currentStops;
  }

  const result = Array.from(currentStops);
  const [removed] = result.splice(startIndex, 1);

  // Only proceed if we actually removed something
  if (removed) {
    result.splice(endIndex, 0, removed);
  }

  // Update the order property for all stops
  return result.map((stop, index) => ({
    ...stop,
    order: index + 1,
  }));
};

/**
 * Reorders VDA actions within a specific mission stop
 */
export const reorderVdaActions = (
  currentStops: MissionStop[],
  startStopIndex: number,
  endStopIndex: number,
  startIndex: number,
  endIndex: number
): MissionStop[] => {
  // Guard: invalid stops array
  if (!Array.isArray(currentStops) || currentStops.length === 0) return [];

  // Guard: validate stop indices
  const isInvalidStopIndex = (i: number) => i < 0 || i >= currentStops.length;
  if (isInvalidStopIndex(startStopIndex) || isInvalidStopIndex(endStopIndex)) {
    return currentStops;
  }

  // If moving within the same stop, keep previous intra-stop behavior
  if (startStopIndex === endStopIndex) {
    const stop = currentStops[startStopIndex];
    const actions = stop.missionStopVdaActions || [];
    if (actions.length === 0) return currentStops;

    if (startIndex < 0 || startIndex >= actions.length) return currentStops;
    if (endIndex < 0 || endIndex >= actions.length) return currentStops;
    if (startIndex === endIndex) return currentStops;

    const copy = [...actions];
    const [removed] = copy.splice(startIndex, 1);
    if (removed) {
      copy.splice(endIndex, 0, removed);
    }
    const updatedStop: MissionStop = {
      ...stop,
      missionStopVdaActions: copy.map((a, i) => ({ ...a, order: i + 1 })),
    };
    return currentStops.map((s, i) => (i === startStopIndex ? updatedStop : s));
  }

  // Moving action between different stops
  const sourceStop = currentStops[startStopIndex];
  const targetStop = currentStops[endStopIndex];
  const sourceActions = sourceStop.missionStopVdaActions || [];
  const targetActions = targetStop.missionStopVdaActions || [];

  // Validate source action index
  if (startIndex < 0 || startIndex >= sourceActions.length) return currentStops;

  // Clamp endIndex to allowable insertion range in target
  const clampedEndIndex = Math.min(Math.max(endIndex, 0), targetActions.length);

  const movingAction = sourceActions[startIndex];
  if (!movingAction) return currentStops;

  // Remove from source
  const newSourceActions = sourceActions
    .filter((_, i) => i !== startIndex)
    .map((a, i) => ({
      ...a,
      order: i + 1,
    }));

  // Insert into target
  const newTargetActions = [...targetActions];
  newTargetActions.splice(clampedEndIndex, 0, movingAction);
  const normalizedTargetActions = newTargetActions.map((a, i) => ({
    ...a,
    order: i + 1,
  }));

  return currentStops.map((stop, idx) => {
    if (idx === startStopIndex) {
      return { ...stop, missionStopVdaActions: newSourceActions };
    }
    if (idx === endStopIndex) {
      return { ...stop, missionStopVdaActions: normalizedTargetActions };
    }
    return stop;
  });
};

/**
 * Validates if all actions in a mission stop are complete
 */
export const areActionsComplete = (missionStop: MissionStop): boolean => {
  // If no actions exist, it's considered complete (station without actions)
  if (
    !missionStop.missionStopVdaActions ||
    missionStop.missionStopVdaActions.length === 0
  ) {
    return true;
  }

  // Check if stop has double confirmation - if so, it's complete
  if (
    missionStop.confirmationType ===
    CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION
  ) {
    return true;
  }

  // Check VDA actions
  for (const action of missionStop.missionStopVdaActions) {
    // Check if action has trigger type
    if (!action.actionParameters || action.actionParameters.length === 0) {
      return false;
    }

    const triggerType = action.actionParameters[0].triggerType;

    // If it's a duration trigger type, check if duration is set and valid
    if (triggerType === TRIGGER_TYPES.DURATION) {
      const duration = action.actionParameters[0].duration;
      if (!duration || duration <= 0) {
        return false;
      }
    }

    // If it's HMI_BUTTON or DOUBLE_CONFIRMATION, they don't need additional validation
    // (HMI_BUTTON and DOUBLE_CONFIRMATION are considered complete if they have the trigger type)
  }

  return true;
};

/**
 * Validates if the current step has incomplete duration actions
 */
export const hasIncompleteDurationActions = (
  missionStop: MissionStop
): boolean => {
  // If no actions exist, no incomplete duration actions
  if (
    !missionStop.missionStopVdaActions ||
    missionStop.missionStopVdaActions.length === 0
  ) {
    return false;
  }

  // Check VDA actions for incomplete duration
  for (const action of missionStop.missionStopVdaActions) {
    if (action.actionParameters && action.actionParameters.length > 0) {
      const triggerType = action.actionParameters[0].triggerType;

      // If it's a duration trigger type, check if duration is zero or invalid
      if (triggerType === TRIGGER_TYPES.DURATION) {
        const duration = action.actionParameters[0].duration;
        if (!duration || duration <= 0) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Builds a comparable fingerprint string from a single stop.
 * Used to detect duplicate stop configurations across versions.
 *
 * Duration is normalized to -1 when confirmationType is not DURATION_RELEASE,
 * mirroring the backend serializer logic.
 *
 * Action parameters within a VDA action are sorted for order-independent
 * comparison — parameter order is not semantically meaningful for
 * fingerprinting purposes.
 */
const buildStopFingerprint = (stop: MissionStop): string => {
  const stopPk = stop.stopPk || "";
  const order = stop.order ?? 0;
  const confirmationType =
    stop.confirmationType || CONFIRMATION_TYPES.CONFIRMATION_TYPE_NONE;
  const duration =
    confirmationType === CONFIRMATION_TYPES.DURATION_RELEASE
      ? stop.duration ?? 0
      : -1;
  const hitchType = stop.hitchType ?? 0;
  const confirmationButtonsRequired = stop.confirmationButtonsRequired ?? 0;

  // Normalize VDA actions
  const actions = [...(stop.missionStopVdaActions || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((action) => {
      const params = JSON.stringify(
        [...(action.actionParameters || [])]
          .map((p) => ({
            triggerType: p.triggerType ?? null,
            duration: p.duration ?? null,
          }))
          .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
      );
      return `${action.actionType || ""}|${
        action.blockingType || ""
      }|${params}|${action.order ?? 0}`;
    })
    .join(";");

  return `${stopPk}:${order}:${duration}:${hitchType}:${confirmationType}:${confirmationButtonsRequired}:${actions}`;
};

/**
 * Checks whether two sets of mission stops represent the same configuration.
 * Returns true if both sets are content-equal.
 *
 * Fingerprints are sorted before comparison so that array order does not
 * affect the result.  Because each fingerprint already encodes the stop's
 * `order` field, reordering stops (changing their `order` values) will
 * produce different fingerprints and therefore a non-equal result.
 */
export const areStopConfigurationsEqual = (
  stopsA: MissionStop[],
  stopsB: MissionStop[]
): boolean => {
  if (stopsA.length !== stopsB.length) return false;

  const fingerprintsA = stopsA.map(buildStopFingerprint).sort();
  const fingerprintsB = stopsB.map(buildStopFingerprint).sort();

  for (let i = 0; i < fingerprintsA.length; i++) {
    if (fingerprintsA[i] !== fingerprintsB[i]) return false;
  }

  return true;
};
